import { Info } from "lucide-react";
import { QuoteCard } from "@/components/QuoteCard";
import { StatCard } from "@/components/StatCard";
import { MetricBar } from "@/components/MetricBar";
import { AuthErrorPrompt } from "@/components/AuthErrorPrompt";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { useUserProfile } from "@/contexts/UserProfileContext";
import { useAuthEnabled } from "@/lib/authQuery";
import {
  useGetDailyQuote,
  useGetQuotesFavorites,
  useGetProfileQuoteClusters,
  getGetQuotesFavoritesQueryKey,
  getGetProfileQuoteClustersQueryKey,
  type QuoteWithTags,
} from "@workspace/api-client-react";

function toCardQuote(q: QuoteWithTags) {
  return {
    id: q.id,
    text: q.text,
    author: q.author,
    era: q.era,
    year: q.year != null ? String(q.year) : q.era,
    tags: q.tags.map((t) => t.name),
  };
}

function isAuthError(error: unknown): boolean {
  return (error as any)?.status === 401 || (error as any)?.status === 403;
}

export function DailyPage() {
  const authed = useAuthEnabled();
  const dailyQuery = useGetDailyQuote();
  const favoritesQuery = useGetQuotesFavorites({
    query: { queryKey: getGetQuotesFavoritesQueryKey(), enabled: authed },
  });
  const clustersQuery = useGetProfileQuoteClusters({
    query: { queryKey: getGetProfileQuoteClustersQueryKey(), enabled: authed },
  });
  const { profile } = useUserProfile();

  if (isAuthError(dailyQuery.error) || isAuthError(favoritesQuery.error)) {
    return <AuthErrorPrompt />;
  }

  const dailyQuote = dailyQuery.data ? toCardQuote(dailyQuery.data) : null;
  const favorites = favoritesQuery.data ?? [];
  const reflectionQuote = favorites.length > 0 ? toCardQuote(favorites[0]) : null;
  const clusters = clustersQuery.data ?? [];
  const topCluster = clusters[0];

  return (
    <section className="grid gap-6 xl:grid-cols-5" data-testid="page-daily">
      <div className="space-y-6 xl:col-span-3">
        {dailyQuery.isLoading ? (
          <div className="rounded-3xl border border-stone-200 bg-white p-12 shadow-sm">
            <p className="animate-pulse text-sm text-stone-400">Loading today's quote…</p>
          </div>
        ) : dailyQuote ? (
          <QuoteCard
            quote={dailyQuote}
            featured
            onDismiss={() => {}}
            dismissConfirmation="Got it — we'll use this to refine your picks."
          />
        ) : (
          <div className="rounded-3xl border border-stone-200 bg-white p-12 shadow-sm">
            <p className="text-sm text-stone-500">No daily quote available yet.</p>
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-3">
          <StatCard
            label="Saved quotes"
            value={favoritesQuery.isLoading ? "…" : String(favorites.length)}
            note={favorites.length === 1 ? "1 author" : `Across ${new Set(favorites.map((q) => q.author)).size} authors`}
          />
          <StatCard
            label="Top theme"
            value={topCluster ? topCluster.label : "—"}
            note={topCluster ? `${topCluster.percentage}% match` : "Save quotes to see"}
          />
          <StatCard
            label="Day streak"
            value={profile ? profile.currentStreak : "…"}
            note={
              profile
                ? profile.currentStreak > 0
                  ? "Keep saving daily"
                  : "Start today"
                : undefined
            }
          />
        </div>
      </div>
      <div className="space-y-6 xl:col-span-2">
        <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-stone-500">From your favorites</p>
              <h3 className="text-lg font-semibold text-stone-900">Reflection quote</h3>
            </div>
            <span className="text-xl text-rose-500">♥</span>
          </div>
          {reflectionQuote ? (
            <QuoteCard quote={reflectionQuote} initialFavorite={true} />
          ) : (
            <p className="text-sm text-stone-400">Save a quote to see your reflection pick here.</p>
          )}
        </div>

        <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm">
          <div className="mb-5 flex items-center gap-1.5">
            <h3 className="text-lg font-semibold text-stone-900">Your quote profile</h3>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label="How these percentages are calculated"
                  className="text-stone-400 hover:text-stone-600 transition-colors"
                >
                  <Info className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-64 text-left">
                These percentages are relative, not absolute — your strongest
                theme is always shown as 100%, and the rest are scaled against
                it. They show how your favorite themes compare to each other,
                not a confidence score.
              </TooltipContent>
            </Tooltip>
          </div>
          {clustersQuery.isLoading ? (
            <p className="animate-pulse text-sm text-stone-400">Loading profile…</p>
          ) : clusters.length > 0 ? (
            <div className="space-y-5">
              {clusters.slice(0, 3).map((c) => (
                <MetricBar key={c.label} label={c.label} value={c.percentage} />
              ))}
            </div>
          ) : (
            <div className="space-y-5">
              <MetricBar label="Stoic / Resilient" value={0} />
              <MetricBar label="Poetic / Reflective" value={0} />
              <MetricBar label="Dark / Existential" value={0} />
            </div>
          )}
          <p className="mt-5 text-sm leading-relaxed text-stone-500">Based only on quotes you have favorited.</p>
        </div>
      </div>
    </section>
  );
}
